import { useCallback, useEffect, useRef, useState } from "react";
import { TIME, TIME_SECONDS } from "../dateTimeUtils";

const zeroIfNaN = value => (Number.isNaN(value) ? 0 : value);

export const useTimer = ({
    time: _time,
    callback,
    interval = TIME_SECONDS.SECOND,
    autoStart = true,
}: {
    time: number;
    callback?: () => void;
    interval?: TIME_SECONDS;
    autoStart?: boolean;
}) => {
    const isCallbackTriggered = useRef(false);

    const calculateTimeRemaining = useCallback(() => {
        const time = _time < 1000000000000 ? _time * 1000 : _time;
        const now = new Date().valueOf();
        const targetDate = new Date(time).valueOf();
        const difference = targetDate - now;

        const isPast = difference < 0;
        const days = Math.floor(difference / TIME.DAY);
        const hours = Math.floor((difference % TIME.DAY) / TIME.HOUR);
        const minutes = Math.floor((difference % TIME.HOUR) / TIME.MINUTE);
        const seconds = Math.floor((difference % TIME.MINUTE) / TIME.SECOND);

        if (isPast) {
            if (!isCallbackTriggered.current && callback) {
                isCallbackTriggered.current = true;
                callback();
            }
            return {
                complete: true,
                days: "00",
                hours: "00",
                minutes: "00",
                seconds: "00",
            };
        }

        return {
            complete: false,
            days: ("00" + zeroIfNaN(days)).slice(zeroIfNaN(days) >= 100 ? -3 : -2),
            hours: ("00" + zeroIfNaN(hours)).slice(-2),
            minutes: ("00" + zeroIfNaN(minutes)).slice(-2),
            seconds: ("00" + zeroIfNaN(seconds)).slice(-2),
        };
    }, [callback, _time]);

    const [timer, setTimer] = useState(calculateTimeRemaining);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        // set time before interval to avoid delay
        setTimer(calculateTimeRemaining());

        if (autoStart) {
            intervalRef.current = setInterval(() => {
                setTimer(calculateTimeRemaining());
            }, interval * TIME.SECOND);

            return () => clearInterval(intervalRef.current);
        }
    }, [_time]);

    return {
        ...timer,
        start: () => {
            intervalRef.current = setInterval(() => {
                setTimer(calculateTimeRemaining());
            }, interval * TIME.SECOND);
        },
        stop: () => {
            clearInterval(intervalRef.current);
        },
        reset: () => {
            clearInterval(intervalRef.current);
            setTimer(calculateTimeRemaining());
        },
    };
};

export const useCountUpTimer = ({
    interval = TIME.SECOND,
    time = null,
    autoStart = true,
}: { interval?: TIME; time?: number; autoStart?: boolean } = {}) => {
    const [elapsedTime, setElapsedTime] = useState<number | null>(time);
    const [isRunning, setIsRunning] = useState(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        return () => {
            clearInterval(intervalRef.current);
        };
    }, []);

    useEffect(() => {
        clearInterval(intervalRef.current);
        setElapsedTime(time);
        if (autoStart && time !== null) {
            start();
        }
    }, [time, autoStart]);

    useEffect(() => {
        clearInterval(intervalRef.current);
        if (autoStart && time !== null) {
            start();
        }
    }, [autoStart]);

    const start = useCallback(() => {
        clearInterval(intervalRef.current);

        intervalRef.current = setInterval(() => {
            setElapsedTime(prev => prev + interval);
        }, interval);
        setIsRunning(true);
    }, [interval]);

    const pause = useCallback(() => {
        clearInterval(intervalRef.current);
        setIsRunning(false);
    }, []);

    const reset = useCallback(() => {
        clearInterval(intervalRef.current);
        setElapsedTime(null);
        start();
    }, [start]);

    const stop = useCallback(() => {
        clearInterval(intervalRef.current);
        setElapsedTime(null);
        setIsRunning(false);
    }, []);

    return {
        elapsedTime,
        elapsedTimeSeconds: Math.floor(elapsedTime / 1000),
        isRunning,
        start,
        pause,
        reset,
        stop,
    };
};

export default useTimer;
